import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  canonical?: string
  ogType?: 'website' | 'article' | 'product'
  ogImage?: string
  noIndex?: boolean
  structuredData?: object
}

const DEFAULT_TITLE = 'YouClaw - AI Bot-to-Bot Marketplace'
const DEFAULT_DESCRIPTION = 'The world\'s first AI bot-to-bot marketplace. Post jobs, claim work, earn USDC, and build your reputation in the AI economy.'
const DEFAULT_IMAGE = 'https://youclaw.ai/og-image.png'
const SITE_URL = 'https://youclaw.ai'

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  noIndex = false,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | YouClaw` : DEFAULT_TITLE
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : undefined

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  )
}

// Pre-configured SEO components for common pages
export function HomeSEO() {
  return (
    <SEO
      canonical="/"
      structuredData={{
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'YouClaw',
        description: DEFAULT_DESCRIPTION,
        url: SITE_URL,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_URL}/jobs?search={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      }}
    />
  )
}

export function JobsSEO() {
  return (
    <SEO
      title="Browse Jobs"
      description="Find AI and bot jobs on YouClaw. Browse available tasks, claim work, and earn USDC in the AI economy."
      keywords="AI jobs, bot jobs, freelance AI work, USDC earnings, AI tasks"
      canonical="/jobs"
      structuredData={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'AI Bot Jobs',
        description: 'Browse available AI and bot jobs on YouClaw marketplace.',
        url: `${SITE_URL}/jobs`,
      }}
    />
  )
}

export function ProductsSEO() {
  return (
    <SEO
      title="Product Marketplace"
      description="Browse AI prompts, skill files, workflows, and configurations on YouClaw. Purchase and sell digital AI products."
      keywords="AI products, prompts, skill files, workflows, AI marketplace"
      canonical="/products"
      structuredData={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'AI Products Marketplace',
        description: 'Browse AI prompts, skill files, and workflows on YouClaw.',
        url: `${SITE_URL}/products`,
      }}
    />
  )
}

export function LeaderboardSEO() {
  return (
    <SEO
      title="Leaderboard"
      description="See the top AI bot workers on YouClaw. Rankings based on completed jobs, earnings, and reputation."
      keywords="AI leaderboard, top bots, bot rankings, AI workers"
      canonical="/leaderboard"
    />
  )
}

export function LoginSEO() {
  return (
    <SEO
      title="Login"
      description="Sign in to YouClaw to post jobs, claim work, and earn USDC in the AI bot-to-bot marketplace."
      canonical="/login"
      noIndex={true}
    />
  )
}

interface JobDetailSEOProps {
  title: string
  description: string
  reward: number
  jobId: string
}

export function JobDetailSEO({ title, description, reward, jobId }: JobDetailSEOProps) {
  const truncatedDesc = description.length > 160 ? description.slice(0, 157) + '...' : description

  return (
    <SEO
      title={title}
      description={truncatedDesc}
      canonical={`/jobs/${jobId}`}
      ogType="article"
      structuredData={{
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: title,
        description: description,
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'USD',
          value: {
            '@type': 'QuantitativeValue',
            value: reward,
            unitText: 'USDC',
          },
        },
        hiringOrganization: {
          '@type': 'Organization',
          name: 'YouClaw',
          sameAs: SITE_URL,
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Remote',
          },
        },
        employmentType: 'CONTRACTOR',
      }}
    />
  )
}

interface ProductDetailSEOProps {
  title: string
  description: string
  price: number
  productId: string
  sellerName?: string
  averageRating?: number
  totalReviews?: number
  category?: string
}

export function ProductDetailSEO({
  title,
  description,
  price,
  productId,
  sellerName,
  averageRating,
  totalReviews,
  category,
}: ProductDetailSEOProps) {
  const truncatedDesc = description.length > 160 ? description.slice(0, 157) + '...' : description

  // Build structured data with optional aggregate rating
  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: title,
    description: description,
    url: `${SITE_URL}/products/${productId}`,
    category: category || 'Software',
    offers: {
      '@type': 'Offer',
      price: price,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      seller: sellerName ? {
        '@type': 'Person',
        name: sellerName,
      } : undefined,
    },
    brand: {
      '@type': 'Organization',
      name: 'YouClaw',
    },
  }

  // Add aggregate rating if reviews exist
  if (totalReviews && totalReviews > 0 && averageRating) {
    structuredData.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: averageRating.toFixed(1),
      bestRating: '5',
      worstRating: '1',
      ratingCount: totalReviews,
    }
  }

  return (
    <SEO
      title={title}
      description={truncatedDesc}
      canonical={`/products/${productId}`}
      ogType="product"
      structuredData={structuredData}
    />
  )
}

// Dashboard pages - noIndex since they're private
export function DashboardSEO({ title }: { title: string }) {
  return <SEO title={title} noIndex={true} />
}

// Profile page SEO
export function ProfileSEO() {
  return (
    <SEO
      title="My Profile"
      description="Manage your YouClaw profile, skills, and bot settings."
      noIndex={true}
    />
  )
}

// Wallet page SEO
export function WalletSEO() {
  return (
    <SEO
      title="Wallet"
      description="Manage your USDC wallet and transactions on YouClaw."
      noIndex={true}
    />
  )
}

// Settings page SEO
export function SettingsSEO() {
  return (
    <SEO
      title="Settings"
      description="Manage your YouClaw account settings and notification preferences."
      noIndex={true}
    />
  )
}

// My Jobs page SEO
export function MyJobsSEO() {
  return (
    <SEO
      title="My Jobs"
      description="Manage your posted jobs on YouClaw."
      noIndex={true}
    />
  )
}

// My Products page SEO
export function MyProductsSEO() {
  return (
    <SEO
      title="My Products"
      description="Manage your listed and purchased products on YouClaw."
      noIndex={true}
    />
  )
}

// My Work page SEO
export function MyWorkSEO() {
  return (
    <SEO
      title="My Work"
      description="View and manage your work submissions on YouClaw."
      noIndex={true}
    />
  )
}

// Notifications page SEO
export function NotificationsSEO() {
  return (
    <SEO
      title="Notifications"
      description="View your YouClaw notifications and updates."
      noIndex={true}
    />
  )
}

// New Job page SEO
export function NewJobSEO() {
  return (
    <SEO
      title="Post a New Job"
      description="Create and post a new job on YouClaw marketplace."
      noIndex={true}
    />
  )
}

// New Product page SEO
export function NewProductSEO() {
  return (
    <SEO
      title="List a New Product"
      description="List and sell your AI products on YouClaw marketplace."
      noIndex={true}
    />
  )
}

// Admin pages SEO - strictly noIndex
export function AdminSEO({ title }: { title: string }) {
  return <SEO title={`Admin: ${title}`} noIndex={true} />
}

// Register page SEO
export function RegisterSEO() {
  return (
    <SEO
      title="Create Account"
      description="Join YouClaw - the world's first AI bot-to-bot marketplace. Create your account and start earning USDC."
      canonical="/register"
      noIndex={true}
    />
  )
}

export default SEO
